/**
 * Creators' platform accounts: data and actions for the accounts page.
 *
 * A wallet may link any number of X accounts and YouTube channels (specification §6.2), and takes
 * part in a campaign with at most five of them (§6.4).
 *
 * Linking leaves the app. The API answers the platform's authorization URL, the browser goes
 * there, and the platform sends it back through the API to `/accounts?linked=…` or
 * `/accounts?error=…`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../api/client.js";

/** A platform a creator can link, as the API names it. */
export type LinkPlatform = "X" | "YOUTUBE";

/** A linked account, as `GET /api/accounts` describes it. */
export interface LinkedAccount {
  platform: LinkPlatform;
  /** The platform's immutable ID: the X user ID or the YouTube channel ID. */
  accountId: string;
  /** The X username, or the channel's handle or title. */
  handle: string;
  displayName: string;
  linkedAt: string;
}

/** `GET /api/accounts`: the signed-in wallet's accounts. */
export interface WalletAccounts {
  /** The accounts linked now, oldest link first. */
  accounts: LinkedAccount[];
}

/** How each platform appears in addresses and on the page, and who authorizes its links. */
export const PLATFORM_DETAILS: Record<
  LinkPlatform,
  { slug: string; name: string; account: string; authorizer: string }
> = {
  X: { slug: "x", name: "X", account: "X account", authorizer: "X" },
  YOUTUBE: { slug: "youtube", name: "YouTube", account: "YouTube channel", authorizer: "Google" },
};

/** Query key of the wallet's accounts in the React Query cache. */
const ACCOUNTS_KEY = ["accounts"] as const;

const accountsQuery = {
  queryKey: ACCOUNTS_KEY,
  queryFn: () => api.get<WalletAccounts>("/api/accounts"),
};

const linkedOnly = (answer: WalletAccounts) => answer.accounts;

/** The signed-in wallet's accounts. */
export function useWalletAccounts() {
  return useQuery(accountsQuery);
}

/** The signed-in wallet's linked accounts. Fetched only once `enabled`, such as when signed in. */
export function useLinkedAccounts(enabled = true) {
  return useQuery({ ...accountsQuery, select: linkedOnly, enabled });
}

/** An account's handle as people write it: `@name` on X. */
export function shownHandle(account: Pick<LinkedAccount, "platform" | "handle">): string {
  return account.platform === "X" ? `@${account.handle}` : account.handle;
}

/**
 * How long the page stays busy once the browser is on its way to the platform. It normally
 * unloads well before. If the creator stops the navigation instead, linking works again after this.
 */
export const LEAVING_MS = 15_000;

/**
 * Starts linking: asks the API for the platform's authorization URL and goes there.
 *
 * The platform's page can take several seconds to open, X's especially. From the click until the
 * browser has left, `busy` names the platform being linked, so that the page shows it and refuses
 * a second start. Every start makes a new authorization, so a second click would restart the
 * trip to the platform, and X may refuse the code it then sends back.
 */
export function useStartLink() {
  const [leaving, setLeaving] = useState<LinkPlatform | null>(null);
  const mutation = useMutation({
    mutationFn: async (platform: LinkPlatform) => {
      const { authorizeUrl } = await api.post<{ authorizeUrl: string }>(
        `/api/accounts/${PLATFORM_DETAILS[platform].slug}/link`,
      );
      setLeaving(platform);
      window.location.assign(authorizeUrl);
    },
  });
  const { reset } = mutation;

  // The page stays busy until it unloads, or for `LEAVING_MS` if it does not.
  useEffect(() => {
    if (leaving === null) {
      return;
    }
    const timer = window.setTimeout(() => setLeaving(null), LEAVING_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);

  // Back from the platform with the browser's back button, the page may be restored as it was
  // left, busy. It is ready for another link then.
  useEffect(() => {
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) {
        setLeaving(null);
        reset();
      }
    }
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [reset]);

  /** The platform being linked, from the click until the browser has left. */
  const busy = leaving ?? (mutation.isPending ? (mutation.variables ?? null) : null);
  return {
    busy,
    /** Starts a link, unless one is already starting. */
    start(platform: LinkPlatform) {
      if (busy === null) {
        mutation.mutate(platform);
      }
    },
    /** Why the last start failed, and on which platform. Null when it did not. */
    failure:
      mutation.isError && mutation.variables
        ? { platform: mutation.variables, error: mutation.error }
        : null,
  };
}

/** The link starter the accounts page shares between its platforms. */
export type LinkStarter = ReturnType<typeof useStartLink>;

/** Unlinks one of the wallet's accounts. */
export function useUnlink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (account: Pick<LinkedAccount, "platform" | "accountId">) =>
      api.delete<void>(
        `/api/accounts/${PLATFORM_DETAILS[account.platform].slug}/${encodeURIComponent(account.accountId)}`,
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

/** What the callback reports, for the creator. */
export const LINK_OUTCOMES: Record<string, string> = {
  LINK_EXPIRED: "The link took too long or was already used. Please try again.",
  SIGN_IN_REQUIRED: "You were signed out. Sign in, then link your account again.",
  WRONG_WALLET:
    "You were signed in with another wallet. Sign in with the wallet that started the link and try again.",
  LINK_CANCELLED: "You declined at the platform, so nothing was linked.",
  SCOPES_MISSING:
    "Morcast needs every permission it asks for, to measure your content. Please link again and allow them all.",
  NO_CHANNEL:
    "That Google account has no YouTube channel. Link again and choose the account that owns your channel.",
  ACCOUNT_IN_CAMPAIGN:
    "That account takes part in a campaign from another wallet. It can move to this wallet once those campaigns are settled, refunded or cancelled.",
  PROVIDER_FAILED:
    "The platform did not complete the link. Wait a minute, then link again with one click.",
  LINK_FAILED: "Linking failed. Please try again.",
};
